import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  Timer, User, Users, BookOpen, ListChecks, CalendarDays,
  BadgeCheck, Clock, Target, GraduationCap,
} from 'lucide-react';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  ServiceHero, JumpNav, SplitSection, StepsSection, CardsSection,
  PanelSection, CompareSection, ChipsSection, CtaBlock, FaqSection, RelatedPages,
  type FaqItem, type RelatedPage,
} from "@/components/service-page/ServicePageSections";

const KEYWORDS = [
  "exam crash courses", "online crash courses", "crash course for exams",
  "intensive exam preparation", "board exam crash course", "class 12 crash course",
];

const EXPLORE_CRASH = { label: "Explore Crash Courses", to: "/search/crash-course" };

const IMG = {
  what:     { src: "/bookmycoaching-crash-teacher-explaining-session-plan.webp", alt: "Teacher explaining a fixed crash course session plan", width: 960, height: 720 },
  why:      { src: "/bookmycoaching-crash-student-joining-online-from-home.webp", alt: "Student joining an online crash course from home", width: 960, height: 1080 },
  how:      { src: "/bookmycoaching-crash-exam-countdown-calendar.webp", alt: "Exam countdown calendar with weak topics noted", width: 720, height: 900 },
  browse:   { src: "/bookmycoaching-crash-search-results-on-bookmycoaching.webp", alt: "Crash course search results on BookMyCoaching", width: 900, height: 560 },
  entrance: { src: "/bookmycoaching-crash-entrance-exam-preparation.webp", alt: "Entrance exam preparation materials", width: 600, height: 300 },
  board:    { src: "/bookmycoaching-crash-board-exam-study-materials.webp", alt: "School board exam study materials", width: 600, height: 300 },
  pro:      { src: "/bookmycoaching-crash-competitive-exam-books.webp", alt: "Competitive exam preparation books", width: 600, height: 300 },
  fees:     { src: "/bookmycoaching-crash-weekly-timetable.webp", alt: "Crash course weekly timetable", width: 900, height: 400 },
  vsCrash:  { src: "/bookmycoaching-crash-timed-exam-practice.webp", alt: "Student doing timed exam practice", width: 800, height: 450 },
  vsOne:    { src: "/bookmycoaching-crash-regular-weekly-tuition.webp", alt: "Regular weekly tuition session", width: 800, height: 450 },
  ready:    { src: "/bookmycoaching-crash-student-ready-for-exams.webp", alt: "Student ready for exams", width: 600, height: 600 },
  faq:      { src: "/bookmycoaching-crash-student-with-a-question.webp", alt: "Student with a question", width: 600, height: 450 },
};

const JUMP_ITEMS = [
  { id: "what",  label: "What is it?" },
  { id: "why",   label: "Why choose it?" },
  { id: "how",   label: "How it works" },
  { id: "browse",label: "Browse courses" },
  { id: "fees",  label: "Fees & sessions" },
  { id: "vs",    label: "vs Other formats" },
  { id: "faq",   label: "FAQs" },
];

const RELATED: RelatedPage[] = [
  {
    label: "1-on-1 Tutoring",
    color: "#2F6BD8",
    icon: User,
    title: "Find 1-on-1 Private Tutors for Personalised Learning",
    excerpt: "One teacher, one student. Lessons built around your subject, level and schedule.",
    to: "/private-tutoring-sessions",
  },
  {
    label: "Group Classes",
    color: "#0E8A78",
    icon: Users,
    title: "Join Group Classes for School, Exams & Skills",
    excerpt: "Shared batches with a fixed timetable and per-student fee. Learn alongside peers.",
    to: "/online-group-classes",
  },
  {
    label: "Become a Tutor",
    color: "#262A7A",
    icon: BookOpen,
    title: "Become an Online Tutor in India with BookMyCoaching",
    excerpt: "Set your fees, choose your schedule and connect with students looking for your expertise.",
    to: "/become-an-online-tutor",
  },
];

const faqs: FaqItem[] = [
  {
    question: "What is an exam crash course?",
    answer: "A crash course is a short, intensive programme that covers selected exam topics in a fixed number of sessions. The teacher sets the timetable, session count and package fee. It is designed for students who need focused revision before an exam date.",
  },
  {
    question: "How long do crash courses last?",
    answer: "It depends on the teacher. Some run for 10 sessions over two weeks; others cover 20 sessions across a month. The listing shows the session count, schedule and start date so you can plan ahead.",
  },
  {
    question: "Can I find crash courses for JEE, NEET and board exams?",
    answer: "Yes. Teachers publish crash courses for a range of exams. Use the search and filter to find courses that match your exam and level.",
  },
  {
    question: "Are crash courses available online?",
    answer: "Yes. Listings show the teaching mode, so you can search for online crash courses in your subject. Check the batch details to confirm the format and timing.",
  },
  {
    question: "Are crash courses solo or group-based?",
    answer: "It depends on the listing. Some teachers run crash courses as group batches with multiple students. Others offer a solo crash course format with one student. The listing states the format clearly.",
  },
  {
    question: "What is included in the crash course fee?",
    answer: "Each listing explains what is covered. Read the listing to see the session count, schedule, exam focus and what the package fee includes before you send an enquiry.",
  },
];

export default function ExamCrashCoursesPage() {
  const { pathname } = useLocation();
  const title = 'Exam Crash Courses: Board & Entrance Prep | BookMyCoaching';
  const description = 'Get exam crash courses with fixed sessions, clear timetables and package fees. Compare board and entrance exam options on BookMyCoaching today.';

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={getCanonicalUrl(pathname)} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={getCanonicalUrl(pathname)} />
        <meta property="og:image" content={getDefaultOgImage()} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <Header />

      <main>
        <ServiceHero
          badge="Crash Courses"
          badgeColor="#D9472B"
          titleLead="Find Crash Courses for"
          titleHighlight="Focused Exam Preparation"
          intro="Short on time before an exam? Find crash courses with fixed sessions, clear timetables and teacher-led plans built around your target. Compare and enquire for free."
          primary={EXPLORE_CRASH}
          secondary={{ label: "How crash courses work", to: "#how" }}
          facts={[
            { icon: ListChecks,   label: "Fixed sessions" },
            { icon: CalendarDays, label: "Clear timetable" },
            { icon: BadgeCheck,   label: "Package fee" },
          ]}
          image="/bookmycoaching-crash-course.gif"
        />

        <JumpNav items={JUMP_ITEMS} />

        {/* What */}
        <SplitSection
          id="what"
          title="What Is a Crash Course?"
          imageLeft
          image={IMG.what}
          paras={[
            "A crash course is an intensive learning programme completed over a shorter, fixed period. The teacher sets the exam focus, the total sessions, the timetable and the full course price.",
            "That makes exam crash courses easy to understand. You see the plan, the dates and the price on the listing before you ask a single question.",
          ]}
          bullets={[
            "A defined start, finish and session count",
            "Learning planned around an exam or subject goal",
            "Concentrated time for revision and practice",
            "Choice of solo or group formats where offered",
          ]}
          paras2={[
            "Every course on BookMyCoaching is published by a teacher, so details like the session count and the exam focus can differ from one listing to the next. Read each one with care.",
          ]}
        />

        {/* Why */}
        <CardsSection
          id="why"
          title="Why Choose a Crash Course for Exam Prep?"
          imageSide="right"
          image={IMG.why}
          intro="A time-bound format gives you a clear framework for focused study, especially close to an exam."
          shaded
          cols={3}
          cards={[
            { icon: Clock,        title: "Defined timeline",         description: "A fixed start, finish and session count. You know exactly how long the course runs." },
            { icon: Target,       title: "Exam-focused syllabus",    description: "Sessions are planned around your target exam — not a general topic list." },
            { icon: CalendarDays, title: "Structured timetable",     description: "A clear schedule removes the guesswork. You know when to study and what is coming next." },
            { icon: ListChecks,   title: "Package price upfront",    description: "One fee for the full course, listed on the page before you enquire." },
            { icon: Users,        title: "Group or solo options",    description: "Some teachers run crash courses as group batches. Others offer individual formats." },
            { icon: GraduationCap,title: "Reviewed teachers",        description: "Teacher profiles are reviewed before approval. Check qualifications and experience on each listing." },
          ]}
          after={[
            "Intensive exam preparation works well because everyone knows the plan. You know when the course starts, how many sessions you get and when it ends. That clarity helps you manage your time.",
          ]}
        />

        {/* How it works */}
        <StepsSection
          id="how"
          title="How Do Crash Courses Work?"
          image={IMG.how}
          level="h3"
          intro="A teacher may offer a fixed number of sessions over days or weeks, with a daily timetable or lessons on selected days."
          steps={[
            { title: "Find a course",      description: "Search for your subject or exam and filter for crash courses." },
            { title: "Read the listing",   description: "Check the session count, dates, schedule, exam focus and package fee." },
            { title: "Send an enquiry",    description: "Create a free account and ask the teacher about details before you enrol." },
            { title: "Enrol and attend",   description: "Agree the next steps with the teacher and begin your revision plan." },
          ]}
          after={[
            "Online crash courses follow the same pattern, with the teacher setting the dates and the package price. Check the batch details to confirm the format.",
          ]}
        />

        {/* Browse */}
        <ChipsSection
          id="browse"
          title="Browse Crash Courses by Exam or Subject"
          image={IMG.browse}
          intro="Find courses matched to your specific exam. What you see depends on the courses teachers have published right now."
          shaded
          groups={[
            {
              icon: GraduationCap,
              title: "Entrance exams",
              image: IMG.entrance,
              chips: [
                { label: "JEE Main",     to: "/search?exam=JEE&format=crash-course" },
                { label: "JEE Advanced", to: "/search?exam=JEE-Advanced&format=crash-course" },
                { label: "NEET",         to: "/search?exam=NEET&format=crash-course" },
                { label: "GATE",         to: "/search?exam=GATE&format=crash-course" },
                { label: "CAT",          to: "/search?exam=CAT&format=crash-course" },
                { label: "UPSC",         to: "/search?exam=UPSC&format=crash-course" },
              ],
            },
            {
              icon: BookOpen,
              title: "Board exams",
              image: IMG.board,
              chips: [
                { label: "Class 12 Physics",    to: "/search?subject=physics&level=12&format=crash-course" },
                { label: "Class 12 Chemistry",  to: "/search?subject=chemistry&level=12&format=crash-course" },
                { label: "Class 12 Maths",      to: "/search?subject=maths&level=12&format=crash-course" },
                { label: "Class 10 Maths",      to: "/search?subject=maths&level=10&format=crash-course" },
                { label: "Class 10 Science",    to: "/search?subject=science&level=10&format=crash-course" },
              ],
            },
            {
              icon: ListChecks,
              title: "Professional & skills",
              image: IMG.pro,
              chips: [
                { label: "IELTS",          to: "/search?exam=IELTS&format=crash-course" },
                { label: "TOEFL",          to: "/search?exam=TOEFL&format=crash-course" },
                { label: "CA Foundation",  to: "/search?exam=CA-Foundation&format=crash-course" },
                { label: "Spoken English", to: "/search?subject=spoken-english&format=crash-course" },
              ],
            },
          ]}
          after={[
            "Online crash courses save travel time, since you join from home. Check the listing for the teaching mode before you enquire.",
          ]}
        />

        {/* Fees panel */}
        <PanelSection
          id="fees"
          title="Crash Course Fees, Sessions and Schedule"
          image={IMG.fees}
          level="h3"
          intro="Each teacher sets their own package fee, session count and timetable. Review the listing fully before you send an enquiry."
          items={[
            "Sessions: Check the total session count (e.g. 10, 15 or 20 sessions)",
            "Dates: Confirm the start date and intensive schedule",
            "Format: Verify whether the course is solo or group-based",
            "Fee: Full package price, listed on the page before you contact the teacher",
          ]}
          boxTitle="Review on every crash course:"
        />

        {/* Compare */}
        <CompareSection
          id="vs"
          title="Crash Courses vs Other Formats"
          level="h3"
          intro="Choose the format that matches your timeline and how you learn best."
          shaded
          cards={[
            {
              icon: Timer,
              title: "Crash Courses",
              description: "Time-bound, exam-focused. Fixed sessions, clear dates and one package fee. Best for targeted revision before an exam.",
              current: true,
              image: IMG.vsCrash,
            },
            {
              icon: User,
              title: "1-on-1 Sessions",
              description: "One teacher, one student. Flexible schedule and ongoing lessons. Ideal for regular study or working through topics at your own pace.",
              link: { label: "Find Private Tutors", to: "/private-tutoring-sessions" },
              image: IMG.vsOne,
            },
          ]}
          after={[
            "If you have a fixed exam date and want a structured push, a crash course gives you a clear plan. If you want ongoing guidance at your own pace, a 1-on-1 session may suit you better.",
          ]}
        />

        <CtaBlock
          title="Find a Crash Course That Fits Your Exam"
          body="Check out the live crash courses and read the listing details — session count, schedule, exam focus and package fee are all shown upfront. Send an enquiry when one feels right for you."
          cta={EXPLORE_CRASH}
          image={IMG.ready}
        />

        <FaqSection faqs={faqs} cta={EXPLORE_CRASH} image={IMG.faq} />

        <RelatedPages pages={RELATED} />
      </main>
      <Footer />
    </div>
  );
}
